import { logError, logDebug } from './loggerService';

/**
 * Service-key-authenticated POSTs to the BE's `/www/*` family.
 *
 * Why this service is separate from `apiService`:
 *   `apiService` is the JWT/cookie-session client for the app shell —
 *   wraps refresh handling, no-network fallback, error-modal hooks, etc.
 *   The `/www/*` family on the BE uses service-key bearer auth
 *   (`Authorization: Bearer <VITE_SERVICE_KEY_WEB>`) and is unauthenticated
 *   from the user's perspective — anyone hitting the marketing site can
 *   submit a contact form. Mixing those two auth modes in `apiService`
 *   would mean conditional headers based on path prefix, which is
 *   exactly the kind of thing that breaks quietly when someone adds a
 *   new endpoint. This is the small, focused alternative.
 *
 * The service-key value comes from `VITE_SERVICE_KEY_WEB`. If it's empty
 * (i.e. nobody set it in `.env.<mode>`), the request still fires; the BE
 * will reject it with 401. Loud failure is better than a silent skip
 * because a stale dev environment otherwise looks "working" until you
 * check the BE logs.
 *
 * Per CLAUDE.md: components must use the corresponding TanStack-Query
 * hook (`useSubmitContactForm` in `src/hooks/useWebForm.ts`) rather than
 * calling these functions directly.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const SERVICE_KEY = import.meta.env.VITE_SERVICE_KEY_WEB || '';
const X_API_KEY = import.meta.env.VITE_X_API_KEY || '';

export interface ContactFormPayload {
  name: string;
  email: string;
  /** The role dropdown's selected option label (e.g. "Eater / neighbor"). */
  role: string;
  message: string;
  /** Optional additional fields the marketing team may add to the form. */
  [extra: string]: unknown;
}

export interface WebFormResponse {
  id: string;
  source: string;
  formData: ContactFormPayload;
  createdAt: string;
  updatedAt: string;
}

/**
 * POST `/www/contact` with the contact form's fields wrapped in `formData`.
 * BE stamps `source = "contact"` server-side; we don't send it.
 *
 * Throws on non-2xx — the hook layer turns this into a `useMutation`
 * error state for the UI to consume.
 */
export async function submitContactForm(
  formData: ContactFormPayload,
): Promise<WebFormResponse> {
  const url = `${API_BASE_URL}/www/contact`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${SERVICE_KEY}`,
  };
  if (X_API_KEY) {
    headers['X-Api-Key'] = X_API_KEY;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ formData }),
    });
  } catch (err) {
    logError(
      err instanceof Error ? err : new Error(String(err)),
      'submitContactForm: fetch threw',
    );
    throw new Error('Network error — please try again in a moment.');
  }

  if (!response.ok) {
    const body = await response.text();
    logError(
      new Error(`POST /www/contact failed: ${response.status}`),
      body,
    );
    if (response.status === 401 || response.status === 403) {
      // Almost always a misconfigured service key in this env.
      throw new Error('Submission rejected by the server — please contact support.');
    }
    throw new Error('Sorry, that didn’t go through. Please try again.');
  }

  const json = (await response.json()) as WebFormResponse;
  logDebug('Contact form submitted', undefined, { id: json.id });
  return json;
}
